import {
  getBackendLanguage,
  hasAnyTranslation,
  translateBackend,
} from '@assets/i18n/config';
import {
  ContextObjectTypes,
  OrganizationRuleGroupCombinator,
} from '@generated/graphql';
import EditIcon from '@mui/icons-material/Edit';
import type { RuleGroupDocument } from '@onevesthq/ov-conditions';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { Link } from '@ovComponents/1-primative/link/link';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { ConditionalRuleBuilder } from '@ovComponents/3-pattern/conditionalRuleBuilder/conditionalRuleBuilder';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import { WorkflowSelect } from '@ovComponents/3-pattern/workflowSelect/workflowSelect';
import { useFeatureFlag } from '@providers/userContextProvider';
import { validateUrl } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import { Box, Button, Typography } from 'hephaestus/web';
import { isNil } from 'lodash-es';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ClientPageInterface,
  PageObjectType,
  type TabInterface,
} from '../pageConfigurationTypes';
import { formatUrl } from './utils';

export const EditTab = ({
  page,
  activeTab,
  setPage,
  hover,
  resetHoverState,
}: {
  page: ClientPageInterface;
  activeTab: number;
  setPage: (page: any) => void;
  hover: boolean;
  resetHoverState: () => void;
}) => {
  const { t } = useTranslation('pageConfiguration');
  const isConditionalPageConfigurationFlagEnabled = useFeatureFlag(
    FlagsmithFlags.conditional_account_page_configuration,
  );
  const [open, setOpen] = useState<boolean>(false);
  const [linkError, setLinkError] = useState<boolean>(false);
  const [workflowError, setWorkflowError] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isRuleGroupValid, setIsRuleGroupValid] = useState<boolean>(true);
  const [tab, setTab] = useState<TabInterface>(page.tabs[activeTab]);
  const [enableLink, setEnableLink] = useState<boolean>(
    translateBackend(page.tabs[activeTab].translatedUrl) !== '',
  );
  const [enableWorkflow, setEnableWorkflow] = useState<boolean>(
    !!page.tabs[activeTab].workflow?.id,
  );
  const [workflowId, setWorkflowId] = useState<string>(
    page.tabs[activeTab].workflow?.id ?? '',
  );

  useEffect(() => {
    if (hover && translateBackend(page.tabs[activeTab].translatedUrl) !== '') {
      setEnableLink(true);
    }
  }, [page, activeTab, hover]);

  const isConditionalPageConfigurationEnabled =
    isConditionalPageConfigurationFlagEnabled &&
    page.type === PageObjectType.ACCOUNT;

  const validateFields = () => {
    let allValid = true;
    const currentLang = getBackendLanguage();

    if (!tab.label[currentLang]) {
      allValid = false;
      setError(true);
    } else {
      setError(false);
    }

    if (enableLink) {
      const isUrlValid = validateUrl(translateBackend(tab.translatedUrl));
      if (!isUrlValid) {
        allValid = false;
        setLinkError(true);
      } else {
        setLinkError(false);
      }
    }

    if (enableWorkflow && !workflowId) {
      allValid = false;
      setWorkflowError(true);
    } else {
      setWorkflowError(false);
    }

    return allValid;
  };

  const savePage = () => {
    const uPage = { ...page, tabs: [...page.tabs] };

    uPage.tabs[activeTab] = {
      ...page.tabs[activeTab],
      label: tab.label,
      icon: tab.icon?.trim() ?? '',
      condition: tab.condition,
      contextObject: tab.contextObject,
      translatedUrl: hasAnyTranslation(tab.translatedUrl)
        ? Object.fromEntries(
            Object.entries(tab.translatedUrl ?? {}).map(([key, value]) => [
              key,
              formatUrl(value),
            ]),
          )
        : undefined,
      workflow: enableWorkflow ? { id: workflowId } : undefined,
    };

    setPage(uPage);
    setError(false);
    setOpen(false);
    setLinkError(false);
    setWorkflowError(false);
    setEnableLink(false);
    setEnableWorkflow(false);
    resetHoverState();
  };

  const onSubmit = () => {
    if (validateFields()) savePage();
  };

  const resetDialogState = () => {
    const currentTab = page.tabs[activeTab];
    setTab(currentTab);
    setError(false);
    setLinkError(false);
    setWorkflowError(false);
    setEnableLink(translateBackend(currentTab.translatedUrl) !== '');
    setEnableWorkflow(!!currentTab.workflow?.id);
    setWorkflowId(currentTab.workflow?.id ?? '');
  };

  const onClose = (event?: any) => {
    event?.stopPropagation();
    setOpen(false);
    resetDialogState();
    resetHoverState();
  };

  const isValid: boolean = /^(?:[a-z]+(?:_[a-z]+)*)?$/.test(
    (tab.icon ?? '').trim(),
  );
  const disabled: boolean =
    !tab.label ||
    (!!tab.icon && !isValid) ||
    (enableLink && !validateUrl(translateBackend(tab.translatedUrl))) ||
    (enableWorkflow && !workflowId) ||
    (isConditionalPageConfigurationEnabled &&
      !!tab.condition?.ruleGroup &&
      !isRuleGroupValid);

  const onClick = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    event?.preventDefault();
    resetDialogState();
    setOpen(true);
  };

  const stopPropagation = (event?: React.MouseEvent) => {
    event?.stopPropagation();
  };

  return (
    <Box
      onClick={stopPropagation}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {hover && <EditIcon sx={{ fontSize: '14px', ml: 1 }} onClick={onClick} />}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={isConditionalPageConfigurationEnabled ? 'sm' : 'xs'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant='headingMedium' sx={{ mr: '4px' }}>
            {t('editTab')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Form onSubmit={onSubmit}>
            <TranslatableTextField
              label={t('label')}
              fullWidth
              error={error}
              value={tab.label}
              onChange={(value) => {
                setTab({ ...tab, label: value });
                setError(!value[getBackendLanguage()]);
              }}
            />
            <TextField
              fullWidth
              label={t('icon')}
              value={tab.icon ?? ''}
              sx={{ mt: 3 }}
              error={!isValid}
              errorText={t('error.invalidIconErrorText')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTab({ ...tab, icon: e.target.value.trim() })
              }
              placeholder='account_balance'
            />
            <Typography variant='labelSmall'>
              <Link
                href='https://mui.com/material-ui/material-icons/'
                target='_blank'
              >
                {t('listOfIcons')}
              </Link>
            </Typography>
            <SwitchField
              label={t('addLinkTab.switch')}
              checked={enableLink}
              onChange={(onoff) => {
                setEnableLink(onoff);
                setLinkError(false);
                if (onoff) {
                  setEnableWorkflow(false);
                  setWorkflowId('');
                  setWorkflowError(false);
                } else {
                  setTab({ ...tab, translatedUrl: getLanguageDefaults() });
                }
              }}
              sx={{ mt: 3 }}
            />
            {enableLink && (
              <TranslatableTextField
                fullWidth
                sx={{ mt: 2 }}
                label={t('addLinkTab.label')}
                value={tab.translatedUrl || getLanguageDefaults()}
                onChange={(value) => setTab({ ...tab, translatedUrl: value })}
                error={linkError}
                errorText={t('addLinkTab.error')}
              />
            )}
            <SwitchField
              label={t('addWorkflowTab.switch')}
              checked={enableWorkflow}
              onChange={(onoff) => {
                setEnableWorkflow(onoff);
                setWorkflowError(false);
                if (onoff) {
                  setEnableLink(false);
                  setTab({ ...tab, translatedUrl: getLanguageDefaults() });
                } else {
                  setWorkflowId('');
                }
              }}
              sx={{ mt: 3 }}
            />
            {enableWorkflow && (
              <Box sx={{ mt: 2 }}>
                <WorkflowSelect
                  label={t('addWorkflowTab.label')}
                  value={workflowId}
                  emptyLabel={t('addWorkflowTab.none')}
                  error={workflowError}
                  onBlur={() => setWorkflowError(!workflowId)}
                  onChange={(e) => {
                    setWorkflowId(e.target.value as string);
                    setWorkflowError(false);
                  }}
                />
              </Box>
            )}

            {isConditionalPageConfigurationEnabled && (
              <ConditionalRuleBuilder
                key={tab.label.en}
                ruleGroup={tab.condition?.ruleGroup as RuleGroupDocument}
                setCondition={(condition) =>
                  setTab((prev) => ({
                    ...prev,
                    condition,
                    contextObject:
                      prev.contextObject ?? ContextObjectTypes.User,
                  }))
                }
                onValidationChange={setIsRuleGroupValid}
                setContextObject={(contextObject: ContextObjectTypes) =>
                  setTab({
                    ...tab,
                    contextObject,
                    condition: {
                      ruleGroup: {
                        combinator: OrganizationRuleGroupCombinator.Or,
                        rules: [],
                      },
                    },
                  })
                }
                {...(!isNil(tab?.contextObject) && {
                  contextObject: tab?.contextObject,
                })}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'end',
              }}
            >
              <Button type='submit' disabled={disabled} sx={{ mt: 3 }}>
                {t('update')}
              </Button>
            </Box>
          </Form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
